import React, { useState, useEffect } from "react";
import apiClient from "../api/apiClient";
import axios from "axios"; // Import axios directly
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const BlogDetails = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      setError(null);

      // Add timeout using axios directly
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        15000
      );

      const response = await apiClient.get(`/blogs/${id}`, {
        cancelToken: source.token,
        timeout: 15000,
      });

      clearTimeout(timeoutId);

      // Validate response
      if (response.data && typeof response.data === "object") {
        setBlog(response.data);
      } else {
        throw new Error("Invalid blog data received");
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        // Use axios.isCancel directly
        setError(new Error("Request timeout: Please check your connection"));
      } else if (err.response) {
        if (err.response.status === 404) {
          setError(
            new Error(
              "Article non trouvé. Cet article peut avoir été supprimé."
            )
          );
        } else {
          setError(
            new Error(
              `Server Error: ${err.response.status} - ${
                err.response.data?.message || "Unknown error"
              }`
            )
          );
        }
      } else if (err.request) {
        setError(
          new Error(
            "Network Error: Unable to reach the server. Please check your connection."
          )
        );
      } else {
        setError(new Error(`Error: ${err.message}`));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlog();
  }, [id, retryCount]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  // Show full page loading
  if (loading && retryCount === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement de l'article...</p>
        </div>
      </div>
    );
  }

  // Show full page error
  if (error && !blog) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="alert alert-danger mt-5" role="alert">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger"></i>
                <h4>Erreur de chargement</h4>
                <p className="mb-3">{error.message}</p>
                <div className="d-flex gap-2 justify-content-center">
                  <button
                    className="btn btn-primary"
                    onClick={handleRetry}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>{" "}
                        Chargement...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-redo me-2"></i> Réessayer
                      </>
                    )}
                  </button>
                  <Link to="/blogs" className="btn btn-outline-primary">
                    <i className="fas fa-arrow-left me-2"></i> Retour aux
                    articles
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show 404 if blog doesn't exist
  if (!blog && !loading) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="alert alert-warning mt-5" role="alert">
              <div className="text-center">
                <i className="fas fa-search fa-3x mb-3 text-warning"></i>
                <h4>Article non trouvé</h4>
                <p className="mb-3">
                  L'article que vous recherchez n'existe pas ou a été supprimé.
                </p>
                <Link to="/blogs" className="btn btn-primary">
                  <i className="fas fa-arrow-left me-2"></i> Retour aux articles
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blog.titre_blog,
    image:
      blog.image_path || "https://www.dvelgegroup.com/images/blogs/default.jpg",
    author: { "@type": "Person", name: blog.auteur || "DVELGE GROUP" },
    publisher: {
      "@type": "Organization",
      name: "DVELGE GROUP",
      logo: {
        "@type": "ImageObject",
        url: "https://www.dvelgegroup.com/images/logo.png",
      },
    },
    datePublished: blog.created_at,
    description: blog.extrait || blog.description_blog?.substring(0, 160) || "",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.dvelgegroup.com/blog-details/${id}`,
    },
  };

  return (
    <div>
      <Helmet>
        <title>{blog.titre_blog} | DVELGE GROUP</title>
        <meta
          name="description"
          content={blog.extrait || "Découvrez notre article sur DVELGE GROUP."}
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.titre_blog} />
        <meta
          property="og:description"
          content={blog.extrait || "Découvrez notre article sur DVELGE GROUP."}
        />
        <meta
          property="og:url"
          content={`https://www.dvelgegroup.com/blog-details/${id}`}
        />
        <meta
          property="og:image"
          content={
            blog.image_path ||
            "https://www.dvelgegroup.com/images/blogs/default.jpg"
          }
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.titre_blog} />
        <meta
          name="twitter:description"
          content={blog.extrait || "Découvrez notre article sur DVELGE GROUP."}
        />
        <meta
          name="twitter:image"
          content={
            blog.image_path ||
            "https://www.dvelgegroup.com/images/blogs/default.jpg"
          }
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Header */}
      <div className="r-bg-a pt85 pb120">
        <div className="container w-992">
          <div className="row pt80">
            <div className="col-lg-12">
              <div className="page-headings text-center">
                <ul className="breadcrus mb20">
                  <li className="bread-non">
                    <Link to="/blogs">Tous les articles</Link>
                  </li>
                  <li className="bread-active">
                    <Link to="#">
                      {blog.libelle_categorie || "Non catégorisé"}
                    </Link>
                  </li>
                </ul>
                <h1>{blog.titre_blog}</h1>
                {/* Show warning if there was an error but we have cached data */}
                {error && blog && (
                  <div className="alert alert-warning mt-3" role="alert">
                    <small>
                      <i className="fas fa-exclamation-triangle me-2"></i>{" "}
                      Données en cache: {error.message}
                      <button
                        className="btn btn-sm btn-outline-primary ms-2"
                        onClick={handleRetry}
                        disabled={loading}
                      >
                        {loading ? "Chargement..." : "Actualiser"}
                      </button>
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blog content */}
      <div className="r-bg-x pb120">
        <div className="container w-992">
          {/* Loading state during retry */}
          {loading && retryCount > 0 ? (
            <div className="row justify-content-center py-5">
              <div className="col-12 text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-3">Mise à jour de l'article...</p>
              </div>
            </div>
          ) : (
            <div className="blog-details">
              <div className="row">
                <div className="col-lg-12">
                  <div className="sol-img mt60">
                    <img
                      src={blog.image_path || "images/blogs/default.jpg"}
                      alt={blog.titre_blog}
                      className="img-fluid"
                      onError={(e) => {
                        e.target.src = "images/blogs/default.jpg";
                        e.target.alt = "Image non disponible";
                      }}
                    />
                  </div>
                  <div className="ree-blog-details">
                    <div className="info-bar mt20 mb30">
                      <div className="info-b-left">
                        <Link to="#">
                          {blog.libelle_categorie || "Non catégorisé"}
                        </Link>
                      </div>
                      <div className="info-b-right">
                        Par <Link to="#">{blog.auteur || "DVELGE GROUP"}</Link>{" "}
                        •{" "}
                        <span>
                          {blog.created_at
                            ? new Date(blog.created_at).toLocaleDateString(
                                "fr-FR"
                              )
                            : "Date non disponible"}
                        </span>
                      </div>
                    </div>
                    <div className="blog-content">
                      {blog.description_blog ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: blog.description_blog,
                          }}
                        />
                      ) : (
                        <div className="alert alert-info">
                          <i className="fas fa-info-circle me-2"></i> Aucun
                          contenu disponible pour cet article.
                        </div>
                      )}
                    </div>
                    <div className="center-btn mt40 text-center">
                      <Link to="/blogs" className="ree-btn ree-btn-grdt2 mr20">
                        <i className="fas fa-arrow-left fa-btn"></i> Retour aux
                        articles
                      </Link>
                      {error && (
                        <button
                          className="btn btn-outline-primary ms-2"
                          onClick={handleRetry}
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>{" "}
                              Actualisation...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-redo me-2"></i> Actualiser
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogDetails;
